import { useCallback, useContext, useEffect, useState } from 'react';

// Hooks
import useAbortController from '../../../../packages/hooks/useAbortController';
import useInterval from '../../../../packages/hooks/useInterval';

// Contexts
import FootballApiContext from '../contexts/FootballApiContext';

// Constants
import refreshTimes from '../../../../packages/constants/refreshTimes';

// Types
import {
  ISubscriberFn,
  ISubscriberInfo,
  IMostDecoratedConfig,
  IMostDecoratedPlayersResponseData,
  IMostDecoratedPlayersResponse,
} from '../../../../packages/types';
import { IGeneralSubscriberInfo } from '../../../../packages/constants/subscribeTypes';
import useSubscriptionClick from '../../../../packages/hooks/useSubscriptionClick';

interface IUseMostDecoratedArgs extends IGeneralSubscriberInfo {
  dataRefreshTime?: IMostDecoratedConfig['dataRefreshTime'];
  dataSeason?: IMostDecoratedConfig['dataSeason'];
  dataLimit?: IMostDecoratedConfig['dataLimit'];
  dataStartPosition?: IMostDecoratedConfig['dataStartPosition'];
}

export default function useMostDecorated({
  dataRefreshTime,
  dataSeason,
  dataWidgetId,
  dataWidgetSport,
  subscribers,
  dataLimit,
  dataStartPosition,
  dataWidgetCuid,
}: IUseMostDecoratedArgs) {
  const [mostDecoratedPlayers, setMostDecoratedPlayers] = useState<IMostDecoratedPlayersResponse[]>();
  const { $mostDecorated } = useContext(FootballApiContext);
  const interval = refreshTimes[dataRefreshTime || 'never'];
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [getSignal] = useAbortController();

  const fetchMostDecorated = useCallback(
    (manageLoader: boolean) => {
      const signal = getSignal();
      manageLoader && setIsLoading(true);

      $mostDecorated
        ?.getMostDecorated({ signal, dataSeason, dataLimit, dataStartPosition })
        ?.then((res: IMostDecoratedPlayersResponseData) => {
          manageLoader && setIsLoading(false);

          if (res.mostDecoratedPlayersError) {
            console.warn('Error while fetching most decorated players: ' + res.mostDecoratedPlayersError);
          }

          setMostDecoratedPlayers(res?.mostDecoratedPlayers);
        })
        .catch((error: unknown) => {
          console.error(error);
        });
    },
    [$mostDecorated, dataSeason, dataLimit, dataStartPosition, getSignal],
  );

  const refetchWidgetData = useCallback(() => {
    fetchMostDecorated(false);
  }, [fetchMostDecorated]);

  useInterval(() => {
    refetchWidgetData();
  }, interval);

  useEffect(() => {
    fetchMostDecorated(true);
  }, [fetchMostDecorated]);

  return {
    widgetData: { mostDecoratedPlayers, isLoading },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
    refetchWidgetData,
  };
}
